import { DatePickerWithRange } from "@/components/shared/form/DateRangePicker";
import { useTranslation } from "react-i18next";
import { usePaginationState, updatePaginationState } from "@/store/paginationStore";
import { normalizeDateToUtcMidnight } from "@/utils/helpers";

/**
 * Configuration interface for the generic DateRangeFilter component
 */
interface DateRangeConfig<QueryVariables> {
  /** The pagination state key (e.g., "paginatedTariffs", "paginatedHolidays") */
  paginationStateKey: keyof PaginationState;
  /** The date field name in the filters (e.g., "activeFrom", "date") */
  dateFieldName: string;
  /** Translation key for the filter text (optional, defaults to "Activation Date") */
  filterTextKey?: string;
  /** Function to build query variables with current filters and date range */
  buildQueryVariables: (
    _displayAmount: number,
    _filters: any,
    _dateRange: { gte: Date | null; lte: Date | null },
    _sort?: any
  ) => QueryVariables;
  /** Function to extract filters from pagination state */
  extractFilters: (_paginationState: any) => any;
}

interface Props<QueryVariables> {
  displayAmount: number;
  handleSubmitClick: (_variables: QueryVariables) => void;
  config: DateRangeConfig<QueryVariables>;
}

/**
 * Generic DateRangeFilter component
 * A reusable date range picker for filtering data by date ranges.
 * Can be configured for different configuration types (tariffs, holidays, etc.)
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - The number of items to display per page
 * @param {Function} props.handleSubmitClick - Callback invoked with updated query variables when date range changes
 * @param {DateRangeConfig} props.config - Configuration object defining the behavior for this specific use case
 * @returns {JSX.Element} A date range picker component
 *
 * @example
 * // For tariffs
 * <DateRangeFilter
 *   displayAmount={20}
 *   handleSubmitClick={handleTariffDateChange}
 *   config={tariffDateConfig}
 * />
 */
const DateRangeFilter = <QueryVariables,>(props: Props<QueryVariables>) => {
  // Props
  const { displayAmount, handleSubmitClick, config } = props;

  // Hooks
  const { t } = useTranslation(["translation", "common", "components"]);

  // Pagination
  const paginationState = usePaginationState(config.paginationStateKey);
  const filters = config.extractFilters(paginationState);
  const currentDateRange = filters[config.dateFieldName] || {};

  /**
   * Handles changing the date range
   * @param {Object} newDateRange - The new date range with from and to dates
   */
  const setDateRange = (newDateRange: { from: Date | undefined; to: Date | undefined } | undefined) => {
    // Handle case where newDateRange is undefined (deselection)
    if (!newDateRange) {
      // Update pagination state with cleared date range (set to null instead of empty object)
      updatePaginationState({
        [config.paginationStateKey]: {
          filters: {
            ...filters,
            [config.dateFieldName]: null
          }
        }
      });

      // Submit the cleared date range
      const currentSort = (paginationState as any)?.sort;
      const baseVariables = config.buildQueryVariables(displayAmount, filters, { gte: null, lte: null }, currentSort);

      const updatedVariables = { ...baseVariables };

      handleSubmitClick(updatedVariables);
      return;
    }

    const dateRange = {
      gte: normalizeDateToUtcMidnight(newDateRange.from),
      lte: normalizeDateToUtcMidnight(newDateRange.to)
    };

    // Update pagination state with new date range
    updatePaginationState({
      [config.paginationStateKey]: {
        filters: {
          ...filters,
          [config.dateFieldName]: dateRange
        }
      }
    });

    // Build and submit query variables with current sort preserved
    const currentSort = (paginationState as any)?.sort;
    const baseVariables = config.buildQueryVariables(displayAmount, filters, dateRange, currentSort);

    // Use the variables as-is since we're now consistently using null
    const updatedVariables = { ...baseVariables };

    handleSubmitClick(updatedVariables);
  };

  return (
    <DatePickerWithRange
      filterText={t(config.filterTextKey || "components:filters.activationdate")}
      dateRange={{
        from: currentDateRange.gte || undefined,
        to: currentDateRange.lte || undefined,
      }}
      setDateRange={setDateRange}
      className="w-full"
      endMonth={new Date(new Date().setFullYear(new Date().getFullYear() + 10))}
    />
  );
};

export default DateRangeFilter;
